import React from 'react';

export interface VerticalResizerProps {
  ariaLabel: string;
  min: number;
  max: number;
  size: number;
  onSizeChange: (next: number) => void;
  reverse?: boolean;
}

export const VerticalResizer: React.FC<VerticalResizerProps> = ({ ariaLabel, min, max, size, onSizeChange, reverse }) => {
  const startXRef = React.useRef<number | null>(null);
  const startSizeRef = React.useRef<number>(size);

  React.useEffect(() => {
    startSizeRef.current = size;
  }, [size]);

  const clamp = React.useCallback((value: number) => {
    return Math.min(max, Math.max(min, value));
  }, [max, min]);

  return (
    <div
      role="separator"
      aria-label={ariaLabel}
      aria-orientation="vertical"
      className="w-1 bg-border hover:bg-muted cursor-col-resize flex-shrink-0"
      onPointerDown={(e) => {
        startXRef.current = e.clientX;
        startSizeRef.current = size;
        (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
        e.preventDefault();
      }}
      onPointerMove={(e) => {
        if (startXRef.current === null) return;
        const delta = e.clientX - startXRef.current;
        const next = reverse ? startSizeRef.current - delta : startSizeRef.current + delta;
        onSizeChange(clamp(next));
      }}
      onPointerUp={(e) => {
        startXRef.current = null;
        try {
          (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
        } catch {
          // ignore
        }
      }}
    />
  );
};
